// /app/frontend/src/components/configurator/ColorZoomModal.jsx
// Modal for viewing color details, editing (admin), and quick actions

import React, { useState } from 'react';
import { Check, Heart, Package, Pencil, Scale, X, Loader2, Flame, Droplets, Ruler, Info, FileText, Maximize2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { resolveImageUrl } from '../../lib/imageCache';
import { MagnifierImage } from './MagnifierImage';
import { FabricLightbox } from './fabric-gallery/FabricLightbox';
import { brandLabel } from '../../lib/brandNames';

const ColorZoomModal = ({
  open,
  onOpenChange,
  zoomedColor,
  config,
  updateConfig,
  selectedFabricMaterial,
  selectedCollection,
  selectedBrand,
  isAdmin,
  editingColor,
  setEditingColor,
  editColorData,
  setEditColorData,
  savingColor,
  saveColorChanges,
  isColorFavorite,
  toggleColorFavorite,
  loadingFavorites,
  isColorSelectedForSample,
  toggleSampleColor
}) => {
  const [enlargedOpen, setEnlargedOpen] = useState(false);

  // Build a lightbox-compatible entry from the zoomed color
  const lightboxEntry = zoomedColor ? {
    id: zoomedColor.id,
    name: zoomedColor.name,
    image_url: zoomedColor.image_url,
    hex_color: zoomedColor.hex_color,
    brand_name: selectedBrand?.name,
    collection_name: selectedCollection?.name,
    collection_website_url: selectedCollection?.website_url,
    stock_status: zoomedColor.stock_status,
    discontinued: zoomedColor.discontinued,
  } : null;

  const startEditingColor = () => {
    if (!zoomedColor || !isAdmin) return;
    setEditColorData({
      name: zoomedColor.name || '',
      code: zoomedColor.code || '',
      hex_color: zoomedColor.hex_color || '',
      image_url: zoomedColor.image_url || ''
    });
    setEditingColor(true);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden">
        <div className="grid md:grid-cols-5">
          {/* Large Image Side - 3/5 width */}
          <div className="relative bg-neutral-100 md:col-span-3 min-h-[400px] md:min-h-[550px]">
            {zoomedColor?.image_url ? (
              <MagnifierImage
                src={resolveImageUrl(zoomedColor.image_url, 1024)}
                alt={zoomedColor.name}
                className="w-full h-full min-h-[400px] md:min-h-[550px]"
              />
            ) : zoomedColor?.hex_color ? (
              <div 
                className="w-full h-full min-h-[400px] md:min-h-[550px] flex items-center justify-center"
                style={{ backgroundColor: zoomedColor.hex_color }}
              >
                <span className="text-white/30 text-6xl font-bold">{zoomedColor.name?.charAt(0)}</span>
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center min-h-[400px]">
                <Package className="w-24 h-24 text-neutral-300" />
              </div>
            )}
            
            {/* Sale Badge */}
            {zoomedColor?.is_on_sale && (
              <span className="absolute top-4 left-4 bg-red-500 text-white text-sm px-3 py-1 rounded-full font-semibold">
                ON SALE
              </span>
            )}
            
            {/* Color preview swatch - bottom left */}
            {zoomedColor?.hex_color && (
              <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2">
                <div 
                  className="w-8 h-8 rounded-md border-2 border-white shadow-md"
                  style={{ backgroundColor: zoomedColor.hex_color }}
                />
                <span className="text-sm font-mono text-neutral-700">{zoomedColor.hex_color}</span>
              </div>
            )}
            {/* Enlarge button - bottom right */}
            {zoomedColor?.image_url && (
              <button
                onClick={() => setEnlargedOpen(true)}
                className="absolute bottom-4 right-4 flex items-center gap-1.5 bg-white/90 backdrop-blur-sm hover:bg-white rounded-lg px-3 py-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 transition-colors shadow-sm border border-white/50"
                data-testid="enlarge-fabric-btn"
              >
                <Maximize2 className="w-4 h-4" />
                Enlarge
              </button>
            )}
          </div>
          
          {/* Details Side - 2/5 width */}
          <div className="p-6 flex flex-col md:col-span-2 max-h-[550px] overflow-y-auto">
            <DialogHeader className="mb-4">
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl font-bold text-neutral-900">
                  {editingColor ? 'Edit Color' : zoomedColor?.name}
                </DialogTitle>
                {isAdmin && !editingColor && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={startEditingColor}
                    className="gap-1"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </Button>
                )}
              </div>
              {zoomedColor?.code && !editingColor && (
                <p className="text-sm text-neutral-500 font-mono">Code: {zoomedColor.code}</p>
              )}
            </DialogHeader>
            
            {editingColor ? (
              /* Admin Edit Mode */
              <div className="space-y-4 flex-1">
                <div>
                  <Label className="text-sm">Color Name</Label>
                  <Input
                    value={editColorData.name}
                    onChange={(e) => setEditColorData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Color name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Color Code</Label>
                  <Input
                    value={editColorData.code}
                    onChange={(e) => setEditColorData(prev => ({ ...prev, code: e.target.value }))}
                    placeholder="e.g. ASP-001"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm">Hex Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={editColorData.hex_color}
                      onChange={(e) => setEditColorData(prev => ({ ...prev, hex_color: e.target.value }))}
                      placeholder="#FFFFFF"
                      className="flex-1"
                    />
                    <input
                      type="color"
                      value={editColorData.hex_color || '#FFFFFF'}
                      onChange={(e) => setEditColorData(prev => ({ ...prev, hex_color: e.target.value }))}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Image URL</Label>
                  <Input
                    value={editColorData.image_url}
                    onChange={(e) => setEditColorData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://..."
                    className="mt-1"
                  />
                </div>
                
                {/* Edit Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setEditingColor(false)}
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    onClick={saveColorChanges}
                    disabled={savingColor}
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                  >
                    {savingColor ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-1" />
                    )}
                    Save Changes
                  </Button>
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
              <div className="space-y-3 flex-1">
                {/* Hierarchy Path */}
                <div className="bg-neutral-50 rounded-lg p-3 text-xs text-neutral-600">
                  {selectedBrand?.name && <span>{brandLabel(selectedBrand.name)}</span>}
                  {selectedCollection?.name && <span> → {selectedCollection.name}</span>}
                  {selectedFabricMaterial?.name && <span> → {selectedFabricMaterial.name}</span>}
                </div>
                
                {/* Technical Specifications */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-neutral-100 px-4 py-2 border-b">
                    <h4 className="font-semibold text-sm text-neutral-800 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Technical Specifications
                    </h4>
                  </div>
                  <div className="p-4 space-y-3">
                    {/* Fabric Type */}
                    {selectedBrand?.fabric_type && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                          <Scale className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Fabric Type</p>
                          <p className="font-medium text-neutral-900">{selectedBrand.fabric_type}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Openness */}
                    {selectedBrand?.openness && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Light Openness</p>
                          <p className="font-medium text-neutral-900">{selectedBrand.openness}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Composition */}
                    {(selectedFabricMaterial?.composition || selectedBrand?.composition) && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                          <Info className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Composition</p>
                          <p className="font-medium text-neutral-900">{selectedFabricMaterial?.composition || selectedBrand?.composition}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Fire Rating */}
                    {(selectedFabricMaterial?.fire_rating || selectedBrand?.fire_rating) && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                          <Flame className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Fire Rating</p>
                          <p className="font-medium text-neutral-900">{selectedFabricMaterial?.fire_rating || selectedBrand?.fire_rating}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Width */}
                    {(selectedFabricMaterial?.width || selectedBrand?.width) && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                          <Ruler className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Max Width</p>
                          <p className="font-medium text-neutral-900">{selectedFabricMaterial?.width || selectedBrand?.width}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Care Instructions */}
                    {(selectedFabricMaterial?.care_instructions || selectedBrand?.care_instructions) && (
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-cyan-50 flex items-center justify-center flex-shrink-0">
                          <Droplets className="w-4 h-4 text-cyan-600" />
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wide">Care Instructions</p>
                          <p className="text-sm text-neutral-700">{selectedFabricMaterial?.care_instructions || selectedBrand?.care_instructions}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Fallback if no specs */}
                    {!selectedBrand?.fabric_type && !selectedBrand?.openness && !selectedFabricMaterial?.composition && !selectedFabricMaterial?.fire_rating && (
                      <p className="text-neutral-400 text-sm italic text-center py-2">Technical specifications coming soon</p>
                    )}
                  </div>
                </div>
              </div>
            
            {/* Actions */}
            <div className="pt-4 border-t border-neutral-200 mt-auto space-y-2">
              {/* Select this color for configuration */}
              <Button
                onClick={() => {
                  if (zoomedColor) {
                    updateConfig('fabric_color', zoomedColor.id);
                    onOpenChange(false);
                    toast.success(`Selected ${zoomedColor.name} for your configuration`);
                  }
                }}
                className={`w-full ${config.fabric_color === zoomedColor?.id ? 'bg-amber-600 hover:bg-amber-700' : 'bg-neutral-900 hover:bg-neutral-800'}`}
              >
                <Check className="w-4 h-4 mr-2" />
                {config.fabric_color === zoomedColor?.id ? 'Currently Selected' : 'Select This Color'}
              </Button>
              <div className="flex gap-2">
                <Button
                  variant={isColorFavorite(zoomedColor?.id) ? 'default' : 'outline'}
                  className={`flex-1 ${isColorFavorite(zoomedColor?.id) ? 'bg-red-500 hover:bg-red-600' : ''}`}
                  onClick={() => zoomedColor && toggleColorFavorite(zoomedColor)}
                  disabled={loadingFavorites}
                  size="sm"
                >
                  <Heart className={`w-4 h-4 mr-1 ${isColorFavorite(zoomedColor?.id) ? 'fill-white' : ''}`} />
                  {isColorFavorite(zoomedColor?.id) ? 'Favorited' : 'Favorite'}
                </Button>
                <Button
                  variant={isColorSelectedForSample(zoomedColor?.id) ? 'default' : 'outline'}
                  className={`flex-1 ${isColorSelectedForSample(zoomedColor?.id) ? 'bg-green-500 hover:bg-green-600' : ''}`}
                  onClick={() => zoomedColor && toggleSampleColor(zoomedColor)}
                  size="sm"
                >
                  <Package className={`w-4 h-4 mr-1`} />
                  {isColorSelectedForSample(zoomedColor?.id) ? 'In Cart' : 'Sample'}
                </Button>
              </div>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="w-full text-neutral-500"
              >
                Close
              </Button>
            </div>
            </>
          )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <FabricLightbox
      entry={enlargedOpen ? lightboxEntry : null}
      open={enlargedOpen}
      onOpenChange={setEnlargedOpen}
    />
    </>
  );
};

export default ColorZoomModal;
